"use client";

import React, { useMemo } from "react";
import { FileText, Database, CornerDownLeft } from "lucide-react";
import type { SearchHit } from "@/services-frontend/search/searchClient";
import { renderIcon } from "@/components/tailwind/editor/renderIcon";
import { cn, formatRelativeDate } from "@/lib/utils";

interface Props {
  hit: SearchHit;
  active: boolean;
  showBodyPreview: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  onBreadcrumbClick?: (crumb: { id: string; title: string }) => void;
  onOwnerClick?: (owner: { id: string; name: string }) => void;
  /**
   * Resolves a hit's `workareaId` into a teamspace crumb. Returns undefined
   * when the id is unknown (e.g. workAreas not loaded, or the user isn't a
   * member) so we simply omit the crumb rather than guess a label.
   */
  resolveWorkArea?: (id: string) => { id: string; name: string } | undefined;
  onWorkAreaClick?: (wa: { id: string; name: string }) => void;
}

/**
 * Renders a single row. The backend already sanitized the snippet (keeps
 * only `<em>` around matched tokens) so it's safe to use
 * dangerouslySetInnerHTML on it — see sanitizeSnippet in the backend
 * searchService.
 */
export function SearchResultItem({
  hit,
  active,
  showBodyPreview,
  onMouseEnter,
  onClick,
  onBreadcrumbClick,
  onOwnerClick,
  resolveWorkArea,
  onWorkAreaClick,
}: Props) {
  const when = useMemo(() => formatRelativeDate(hit.updatedAt), [hit.updatedAt]);

  // Content-block matches need a different layout to feel right:
  //
  //   - The indexer stores the block's content text into the `title` field
  //     (content blocks don't carry their own title). Rendering that as the
  //     row heading reads as if the page itself was named after a chunk of
  //     prose — which is what made the old layout look like "content line,
  //     then breadcrumb, then snippet, then snippet again".
  //   - The pattern is: heading = the parent page name, meta = the
  //     ancestors / owner / edited, snippet = the matched content.
  //
  // We rebuild those three values for content hits and fall back to the
  // existing single-field render for page / database hits.
  const isContent = hit.type === "content";
  const parentCrumb =
    hit.breadcrumb.length > 0 ? hit.breadcrumb[hit.breadcrumb.length - 1] : undefined;

  // For content hits, promote the parent page name into the heading. The
  // promoted text is plain (it never carried `<em>` markers), so render it
  // as text instead of HTML.
  const headingHtml = isContent && parentCrumb?.title ? null : hit.title;
  const headingText = isContent && parentCrumb?.title ? parentCrumb.title : null;

  // The meta breadcrumb shows the *ancestors* — drop the immediate parent
  // since it now lives in the heading. For page / database hits we keep the
  // full chain unchanged.
  const metaBreadcrumb = isContent ? hit.breadcrumb.slice(0, -1) : hit.breadcrumb;

  // Teamspace crumb — rendered first, and shown even when there are no page
  // ancestors so a workArea-root page reads as "[Teamspace]". Resolved from the
  // hit's workareaId at render time, so it's uniform across the visit log,
  // search results, and the API-recent list without baking it into each row.
  const workArea = hit.workareaId ? resolveWorkArea?.(hit.workareaId) : undefined;

  // Body line: prefer the dedicated highlighted snippet from Meilisearch.
  // For content hits without a body snippet, fall back to the raw content
  // captured in `hit.title` so the row still shows what matched.
  const bodySnippet = hit.snippet ?? (isContent ? hit.title : undefined);
  const showSnippet = showBodyPreview && Boolean(bodySnippet);

  return (
    <div
      role="option"
      aria-selected={active}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 transition-colors",
        active
          ? "bg-gray-100 dark:bg-[rgb(42,42,42)]"
          : "hover-component ",
      )}
    >
      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
        <ResultIcon hit={hit} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {headingText !== null ? (
            <div className="truncate text-sm font-medium text-foreground">
              {headingText}
            </div>
          ) : (
            <div
              className="truncate text-sm font-medium text-foreground"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: headingHtml ?? "New page" }}
            />
          )}
          {/* {hit.visibility === "private" ? (
            <span className="rounded bg-gray-100 dark:bg-[rgb(42,42,42)] px-1.5 py-0.5 text-[10px] text-muted-foreground">
              Private
            </span>
          ) : null} */}
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
          {workArea || metaBreadcrumb.length > 0 ? (
            <div className="flex items-center gap-1 min-w-0 truncate">
              {workArea ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onWorkAreaClick?.(workArea);
                  }}
                  className="truncate max-w-[120px] hover:underline"
                >
                  {workArea.name}
                </button>
              ) : null}
              {workArea && metaBreadcrumb.length > 0 ? <span>/</span> : null}
              {metaBreadcrumb.slice(-3).map((crumb, idx, arr) => (
                <React.Fragment key={crumb.id}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBreadcrumbClick?.(crumb);
                    }}
                    className="truncate max-w-[120px] hover:underline"
                  >
                    {crumb.title || "New page"}
                  </button>
                  {idx < arr.length - 1 ? <span>/</span> : null}
                </React.Fragment>
              ))}
              {/* Trailing separator only when an owner / edited-time follows —
                  visit-log rows (no owner, no timestamp) would otherwise show a
                  dangling bullet after the breadcrumb. */}
              {hit.owner.name || when ? <span>•</span> : null}
            </div>
          ) : null}

          {hit.owner.name ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOwnerClick?.({ id: hit.owner.id, name: hit.owner.name });
              }}
              className="hover:underline"
            >
              {hit.owner.name}
            </button>
          ) : null}

          {hit.owner.name && when ? <span>•</span> : null}
          {when ? <span>Edited {when}</span> : null}
        </div>

        {showSnippet ? (
          <div
            className="mt-1 line-clamp-2 text-xs text-muted-foreground"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: bodySnippet ?? "" }}
          />
        ) : null}
      </div>

      <div className={cn("mt-1 shrink-0 transition-opacity", active ? "opacity-100" : "opacity-0")}>
        <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </div>
  );
}

function ResultIcon({ hit }: { hit: SearchHit }) {
  // Same renderer as the sidebar, breadcrumbs and notifications, so an Iconify
  // id ("tabler:home") draws its glyph rather than falling through to the
  // generic type icon below. `rounded` is inherited by uploaded image icons,
  // which render with borderRadius: "inherit".
  const icon = hit.icon?.trim();
  if (icon) {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded leading-none">
        {renderIcon(icon, { style: { width: "100%", height: "100%", fontSize: 16 } })}
      </span>
    );
  }
  if (hit.type === "database") return <Database className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
}
